import { Button, InputWrapper } from '@ibsys/kit';
import { FC } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { TruncatedText } from '@/shared/ui';
import { describePhrase } from './recordLabels';
import { useTranscriptEditorContext } from './TranscriptEditorContext';
import type { InterruptionPhraseField, TranscriptRecordFormValues } from './types';

/** Соседнее поле перебивания: с ним выбранная реплика не должна совпадать */
const OTHER_PHRASE_FIELD: Record<InterruptionPhraseField, InterruptionPhraseField> = {
  interruptedPhraseId: 'interruptionPhraseId',
  interruptionPhraseId: 'interruptedPhraseId',
};

type Props = {
  name: InterruptionPhraseField;
  label: string;
};

/**
 * Реплика перебивания: кнопка поля включает выбор, и нажатие на реплику в ленте записывает её в поле.
 */
export const TranscriptPhrasePickField: FC<Props> = ({ name, label }) => {
  const { t } = useTranslation(['dialogs', 'common']);
  const { control, getValues, trigger } = useFormContext<TranscriptRecordFormValues>();
  const { field, fieldState } = useController({ control, name });
  const { phrasesById, phrasePick, startPhrasePick, stopPhrasePick } = useTranscriptEditorContext();

  const isPicking = phrasePick?.field === name;
  const phrase = field.value === null ? undefined : phrasesById.get(field.value);

  const handlePickToggle = () => {
    if (isPicking) {
      stopPhrasePick();
      return;
    }
    startPhrasePick({
      field: name,
      onPick: (phraseId) => {
        field.onChange(phraseId);
        stopPhrasePick();
        // Совпадение с соседним полем видно сразу, а не только при сохранении; пустое соседнее
        // поле при этом не подсвечивается — его ещё не заполняли
        if (getValues(OTHER_PHRASE_FIELD[name]) !== null) {
          void trigger(['interruptedPhraseId', 'interruptionPhraseId']);
        }
      },
    });
  };

  return (
    <InputWrapper label={label} error={fieldState.error?.message} required>
      <div className="flex items-center gap-2 min-w-0">
        <TruncatedText className="flex-1 text-sm">
          {phrase ? describePhrase(phrase, t) : t('dialogs:transcriptEdit.phraseNotSelected')}
        </TruncatedText>
        <Button
          type="button"
          size="sm"
          variant={isPicking ? 'default' : 'outline'}
          aria-pressed={isPicking}
          className="shrink-0"
          onClick={handlePickToggle}
        >
          {isPicking ? t('dialogs:transcriptEdit.pickingPhrase') : t('dialogs:transcriptEdit.pickPhrase')}
        </Button>
      </div>
    </InputWrapper>
  );
};
